// Team + Tables (Lists) navigation block of the workspace-scope sidebar —
// extracted from workspace-content.tsx during the P5 split. Pure render.

import {
  Plus,
  CaretRight,
  UsersThree,
  ListBullets,
  Lightning,
} from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { NavRow } from "@/components/ui/nav-row";
import { NavItem } from "../NavItem";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import { Button } from "@/components/ui/button";

// Tables nav hidden behind a constant flag while the feature is paused.
// Flip back to `true` to restore the sidebar entry.
const SHOW_TABLES_NAV = false;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ListLike = any;

interface WorkspaceTablesNavProps {
  isCollaborator: boolean;
  /**
   * Whether the current user may reach workspace admin surfaces in this nav —
   * Team (member roster), Automations, and Tables/Lists. False for regular
   * (non-admin) members of a PUBLIC workspace, so a drive-by joiner can't see
   * the membership, manage automations, or edit tables. Defaults to true
   * (private workspaces and admins are unchanged).
   */
  canAccessTeam?: boolean;
  showCoworkers: boolean | undefined;
  onToggleCoworkers: (() => void) | undefined;
  showAutomations: boolean | undefined;
  onToggleAutomations: (() => void) | undefined;
  onToggleLists: (() => void) | undefined;
  showLists: boolean | undefined;
  selectedListId: string | null | undefined;
  onSelectList: ((id: string) => void) | undefined;
  onCreateListItem: ((preselectedListId?: string) => void) | undefined;
  workspaceLists: ListLike[];
  listsOpen: boolean;
  setListsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function WorkspaceTablesNav({
  isCollaborator,
  canAccessTeam = true,
  showCoworkers,
  onToggleCoworkers,
  showAutomations,
  onToggleAutomations,
  onToggleLists,
  showLists,
  selectedListId,
  onSelectList,
  onCreateListItem,
  workspaceLists,
  listsOpen,
  setListsOpen,
}: WorkspaceTablesNavProps) {
  return (
    <div className="px-2 pt-0 pb-2 space-y-0.5">
      {!isCollaborator && canAccessTeam && (
        <NavItem
          icon={UsersThree}
          label="Team"
          isActive={showCoworkers}
          onClick={() => {
            if (!showCoworkers) {
              onToggleCoworkers?.();
            } else {
              window.dispatchEvent(new CustomEvent("ano:team-navigate-table"));
            }
          }}
        />
      )}
      {!isCollaborator && canAccessTeam && (
        <NavItem
          icon={Lightning}
          label="Automations"
          isActive={showAutomations}
          onClick={() => {
            if (!showAutomations) onToggleAutomations?.();
          }}
        />
      )}
      {/* Lists */}
      {SHOW_TABLES_NAV && !isCollaborator && canAccessTeam && (
        <div>
          <div className="group/tables relative">
            <NavRow
              isSelected={showLists && !selectedListId}
              onClick={onToggleLists}
              className={cn(
                "hover:bg-foreground/[0.025]",
                workspaceLists.length > 0 && "pr-8",
              )}
            >
              <ListBullets size={16} />
              <span className="min-w-0 flex-1 truncate text-left">Tables</span>
            </NavRow>
            {workspaceLists.length > 0 && (
              <ToolbarIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setListsOpen((v) => !v);
                }}
                className="absolute right-1 top-1/2 z-10 h-6 w-6 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground"
                aria-label={listsOpen ? "Collapse tables" : "Expand tables"}
              >
                <CaretRight
                  size={10}
                  weight="Bold"
                  className={cn(
                    "transition-transform duration-150",
                    listsOpen && "rotate-90",
                  )}
                />
              </ToolbarIconButton>
            )}
          </div>
          {listsOpen && workspaceLists.length > 0 && (
            <div className="space-y-0.5">
              {workspaceLists.slice(0, 3).map((list: ListLike) => (
                <div key={list.id} className="group/list-item relative">
                  <NavRow
                    isSelected={selectedListId === list.id}
                    onClick={() => onSelectList?.(list.id)}
                    className={cn(
                      "pl-7 pr-8 transition-colors hover:bg-foreground/[0.025]",
                      selectedListId === list.id
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate text-left">
                      {list.name}
                    </span>
                  </NavRow>
                  <ToolbarIconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateListItem?.(list.id);
                    }}
                    className="absolute right-1 top-1/2 z-10 h-6 w-6 -translate-y-1/2 shrink-0 text-muted-foreground/50 opacity-0 transition-opacity group-hover/list-item:opacity-100 hover:text-muted-foreground"
                    aria-label={`Add item to ${list.name}`}
                  >
                    <Plus size={12} weight="Bold" />
                  </ToolbarIconButton>
                </div>
              ))}
              {workspaceLists.length > 3 && (
                <Button
                  type="button"
                  variant="link"
                  onClick={onToggleLists}
                  className="h-auto w-full justify-start py-1 pl-7 text-[12px] text-muted-foreground/50"
                >
                  View all ({workspaceLists.length})
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
